import { useMemo } from 'react';
import {
  ColorRepresentation,
  EdgesGeometry,
  LineDashedMaterial,
  LineSegments,
  MeshBasicMaterial,
  Vector3Tuple,
} from 'three';

import { Line } from '@react-three/drei';
import { GroupProps } from '@react-three/fiber';
import { NavMesh } from '@recast-navigation/core';
import { NavMeshHelper as RecastNavMeshHelper } from '@recast-navigation/three';

export interface NavMeshHelperProps extends Omit<GroupProps, 'navMesh'> {
  navMesh: NavMesh;
  color?: ColorRepresentation;
  lineWidth?: number;
}

export const NavMeshHelper: React.FC<NavMeshHelperProps> = ({
  navMesh,
  color = 0x00ff00,
  lineWidth = 4,
  ...restProps
}) => {
  const navMeshHelper = useMemo(() => {
    return new RecastNavMeshHelper({
      navMesh,
      navMeshMaterial: new MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.2,
      }),
    });
  }, [color, navMesh]);

  const points = useMemo<Vector3Tuple[]>(() => {
    const lineSegments = new LineSegments(
      new EdgesGeometry(navMeshHelper.geometry),
      new LineDashedMaterial({
        color,
        vertexColors: true,
        linewidth: lineWidth,
        scale: 1,
        dashSize: 1,
        gapSize: 1,
      })
    );
    lineSegments.computeLineDistances();

    const points = lineSegments.geometry.attributes.position.array;

    const result: Vector3Tuple[] = [];
    for (let i = 0; i < points.length; i += 3) {
      result.push([points[i], points[i + 1], points[i + 2]]);
    }
    return result;
  }, [color, lineWidth, navMeshHelper.geometry]);

  return (
    <group {...restProps}>
      <primitive dispose={null} object={navMeshHelper} />
      <Line segments color={color} dashed={false} lineWidth={2} points={points} />
    </group>
  );
};
